import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import Box from "../Box/Box";
import Popup from "../Popup/Popup";
import { Title, Paragraph } from "../Styled/Text";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ContactManagerQuery = {
  translationsJson: {
    pages: {
      configurator: {
        contact_manager: {
          title: string[];
          description: string;
          phone: string;
          phoneHref: string;
          email: string;
          emailHref: string;
          telegramUrl: string;
          maxUrl: string;
        };
      };
    };
  };
};

const GOLD = "#B8AB94";
const BG = "#433934";

const ContactManagerPopup = ({ isOpen, onClose }: Props) => {
  const data = useStaticQuery<ContactManagerQuery>(graphql`
    query ContactManagerPopupQuery {
      translationsJson {
        pages {
          configurator {
            contact_manager {
              title
              description
              phone
              phoneHref
              email
              emailHref
              telegramUrl
              maxUrl
            }
          }
        }
      }
    }
  `);

  const content = data.translationsJson.pages.configurator.contact_manager;

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      backgroundColor={BG}
      color={GOLD}
      closeColor={GOLD}
      zIndex={1000}
    >
      <Title
        as="h2"
        variant="hero"
        sx={{
          fontSize: ["33px", "33px", "58px", "58px"],
          lineHeight: ["38px", "38px", "58px", "58px"],
          textAlign: "center",
          color: GOLD,
          margin: 0,
        }}
      >
        {content.title.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </React.Fragment>
        ))}
      </Title>

      <Paragraph
        sx={{
          fontSize: ["18px", "18px", "22px", "22px"],
          lineHeight: ["28px", "28px", "36px", "36px"],
          color: GOLD,
          opacity: 1,
          margin: "10px 0 0 0",
          maxWidth: ["322px", "322px", "673px", "673px"],
          padding: 0,
          textAlign: "center",
        }}
      >
        {content.description}
      </Paragraph>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: ["20px", "20px", "30px", "30px"],
          marginTop: "23px",
          width: "246px",
        }}
      >
        <Box
          as="a"
          href={content.phoneHref}
          sx={{
            fontFamily: "custom_42266",
            fontWeight: 400,
            fontSize: ["23px", "23px", "32px", "32px"],
            lineHeight: "22px",
            textAlign: "center",
            color: GOLD,
            textDecoration: "none",
          }}
        >
          {content.phone}
        </Box>
        <Box
          as="a"
          href={content.emailHref}
          sx={{
            fontFamily: "custom_42266",
            fontWeight: 400,
            fontSize: ["23px", "23px", "32px", "32px"],
            lineHeight: "22px",
            textAlign: "center",
            color: GOLD,
            textDecoration: "none",
          }}
        >
          {content.email}
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-start",
          }}
        >
          <Box
            as="a"
            href={content.telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Telegram"
            sx={{
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              as="img"
              src="/images/social/telegram.svg"
              alt="Telegram"
              sx={{ width: "36px", height: "36px", display: "block" }}
            />
          </Box>
          <Box
            as="a"
            href={content.maxUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="MAX"
            sx={{
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              as="img"
              src="/images/social/max.svg"
              alt="MAX"
              sx={{ width: "36px", height: "36px", display: "block" }}
            />
          </Box>
        </Box>
      </Box>
    </Popup>
  );
};

export default React.memo(ContactManagerPopup);
